import { Typography } from '@/components/atoms/Typography';
import { UserIcon, UserStarIcon } from '@/components/atoms/icons';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React from 'react';
import { StyleSheet, View } from 'react-native';

export interface OwnerItemProps {
    id: string;
    name: string;
    share: number;
    isPrimary?: boolean;
}

export function OwnerItem({
    id,
    name,
    share,
    isPrimary = false,
}: OwnerItemProps) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();

    return (
        <View style={styles.container}>
            <View style={styles.content}>
                {/* Owner Icon */}
                {isPrimary ? (
                    <UserStarIcon size={20} color={colors.fontPrimary} />
                ) : (
                    <UserIcon size={20} color={colors.fontPrimary} />
                )}

                {/* Owner Info */}
                <View style={styles.ownerInfo}>
                    <Typography 
                        variant="headline" 
                        color="primary" 
                        style={[styles.ownerName, isRTL && styles.ownerNameRTL]}
                    >
                        {name}
                    </Typography>
                    <Typography 
                        variant="footnote" 
                        color="secondary" 
                        style={[styles.ownerShare, isRTL && styles.ownerShareRTL]}
                    >
                        {t('propertyDetails.share')}: {share}%
                    </Typography>
                </View>

                {/* Primary Tag */}
                {isPrimary && (
                    <View style={[
                        styles.primaryTag,
                        {
                            backgroundColor: 'transparent', // Transparent background
                            borderColor: colors.fontPrimary, // Black border
                        }
                    ]}>
                        <Typography variant="footnote" color="primary" style={styles.primaryTagText}>
                            {t('propertyDetails.primary')}
                        </Typography>
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        paddingVertical: 8,
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    ownerInfo: {
        flex: 1,
        gap: 2,
    },
    ownerName: {
        fontSize: 16,
        fontWeight: '600',
    },
    ownerNameRTL: {
        textAlign: 'right',
    },
    ownerShare: {
        fontSize: 13,
        fontWeight: '400',
    },
    ownerShareRTL: {
        textAlign: 'right',
    },
    primaryTag: {
        paddingVertical: 6,
        paddingHorizontal: 8,
        borderRadius: 8,
        borderWidth: 1,
    },
    primaryTagText: {
        fontSize: 14,
        fontWeight: '500',
        fontFamily: 'Matter',
    },
});

export default OwnerItem;
